import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Checkbox,
  Radio,
  Space,
  Steps,
} from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  ArrowLeftOutlined,
  BankOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../store/context/theme-context";
import { useNotification } from "../../store/context/notification-context";
import { useTenant } from "../../store/context/tenant-context";
import LanguageSelector from "../../shared/languages/LanguageSelector";
import TenantSwitcher from "../../components/General/TenantSwitcher";

const { Title, Text, Link } = Typography;
const { Step } = Steps;

const Signup = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { theme } = useTheme();
  const { triggerNotification } = useNotification();

  const isDark = theme === "dark";

  const [currentStep, setCurrentStep] = useState(0);
  const [accountType, setAccountType] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1: Account Type Selection
    accountType: null,

    // Step 2: Personal Information
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",

    // Step 3: Account Details
    username: "",
    password: "",
    confirmPassword: "",

    // Terms
    agreeToTerms: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccountTypeSelect = (type) => {
    setAccountType(type);
    setFormData((prev) => ({ ...prev, accountType: type }));
    localStorage.setItem("accountType", type);
  };

  const handleNext = async () => {
    try {
      // Validate current step
      if (currentStep === 0) {
        if (!accountType) {
          triggerNotification({
            type: "warning",
            message: "Select Account Type",
            description: "Please select an account type to continue.",
          });
          return;
        }
        setCurrentStep(1);
      } else if (currentStep === 1) {
        // Validate personal information
        await form.validateFields([
          "firstName",
          "lastName",
          "email",
          "phoneNumber",
        ]);
        setCurrentStep(2);
      }
    } catch (error) {
      // Form validation failed
      console.error("Validation failed:", error);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      // Validate all fields
      await form.validateFields();

      // Check if passwords match
      if (formData.password !== formData.confirmPassword) {
        triggerNotification({
          type: "error",
          message: "Passwords Don't Match",
          description: "Please ensure both passwords are the same.",
        });
        setIsLoading(false);
        return;
      }

      // Check if user agreed to terms
      if (!formData.agreeToTerms) {
        triggerNotification({
          type: "warning",
          message: "Accept Terms",
          description: "Please accept the terms and conditions to continue.",
        });
        setIsLoading(false);
        return;
      }

      // Prepare data for API
      const dataToPost = {
        accountType: formData.accountType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        username: formData.username,
        password: formData.password,
      };

      // TODO: Call signup API
      console.log("Signup data:", dataToPost);

      // Navigate to existing signup flow
      navigate("/authentication?mode=signup", {
        state: { formData: dataToPost },
      });

      triggerNotification({
        type: "success",
        message: "Account Created",
        description: "Your account has been created successfully!",
      });
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Signup Failed",
        description: "An error occurred during signup. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderAccountTypeSelection = () => (
    <div style={{ marginTop: "8px" }}>
      {/* Helpful Header */}
      <div style={{ 
        textAlign: "center", 
        marginBottom: "32px",
        padding: "20px",
        backgroundColor: isDark ? "rgba(24, 144, 255, 0.1)" : "rgba(24, 144, 255, 0.05)",
        borderRadius: "12px",
        border: `1px solid ${isDark ? "rgba(24, 144, 255, 0.2)" : "rgba(24, 144, 255, 0.1)"}`,
      }}>
        <Title
          level={4}
          style={{
            color: isDark ? "#ffffff" : "#000000",
            marginBottom: "8px",
            fontSize: "20px",
          }}
        >
          Let's get started! 🚀
        </Title>
        <Text style={{ 
          color: isDark ? "#a0a0a0" : "#666666",
          fontSize: "14px",
        }}>
          Choose the account type that best suits your needs. You can upgrade or switch later.
        </Text>
      </div>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Personal Account Card */}
        <div
          onClick={() => handleAccountTypeSelect("Personal")}
          style={{
            border: `2px solid ${
              accountType === "Personal"
                ? tenant.theme.colorPrimary
                : isDark
                ? "#303030"
                : "#d9d9d9"
            }`,
            borderRadius: "12px",
            padding: "24px",
            cursor: "pointer",
            backgroundColor:
              accountType === "Personal"
                ? isDark
                  ? "rgba(24, 144, 255, 0.08)"
                  : "rgba(24, 144, 255, 0.05)"
                : isDark ? "rgba(0, 0, 0, 0.2)" : "transparent",
            transition: "all 0.3s ease",
            boxShadow: accountType === "Personal" ? `0 4px 12px ${tenant.theme.colorPrimary}40` : "none",
          }}
          onMouseEnter={(e) => {
            if (accountType !== "Personal") {
              e.currentTarget.style.borderColor = tenant.theme.colorPrimary;
              e.currentTarget.style.transform = "translateY(-2px)";
            }
          }}
          onMouseLeave={(e) => {
            if (accountType !== "Personal") {
              e.currentTarget.style.borderColor = isDark ? "#303030" : "#d9d9d9";
              e.currentTarget.style.transform = "translateY(0)";
            }
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "12px",
                backgroundColor: accountType === "Personal" ? tenant.theme.colorPrimary : isDark ? "#303030" : "#e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
              }}
            >
              <UserOutlined style={{ fontSize: "28px", color: "#ffffff" }} />
            </div>
            <div style={{ flex: 1 }}>
              <Title
                level={5}
                style={{
                  margin: "0 0 6px 0",
                  color: isDark ? "#ffffff" : "#000000",
                  fontSize: "18px",
                }}
              >
                Personal Account
              </Title>
              <Text style={{ 
                color: isDark ? "#a0a0a0" : "#666666",
                fontSize: "14px",
                display: "block",
                marginBottom: "8px",
              }}>
                Perfect for individuals managing personal finances
              </Text>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Quick setup
                </Text>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Instant transfers
                </Text>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Bill payments
                </Text>
              </div>
            </div>
            <Radio checked={accountType === "Personal"} />
          </div>
        </div>

        {/* Corporate Account Card */}
        <div
          onClick={() => handleAccountTypeSelect("Corporate")}
          style={{
            border: `2px solid ${
              accountType === "Corporate"
                ? tenant.theme.colorPrimary
                : isDark
                ? "#303030"
                : "#d9d9d9"
            }`,
            borderRadius: "12px",
            padding: "24px",
            cursor: "pointer",
            backgroundColor:
              accountType === "Corporate"
                ? isDark
                  ? "rgba(24, 144, 255, 0.08)"
                  : "rgba(24, 144, 255, 0.05)"
                : isDark ? "rgba(0, 0, 0, 0.2)" : "transparent",
            transition: "all 0.3s ease",
            boxShadow: accountType === "Corporate" ? `0 4px 12px ${tenant.theme.colorPrimary}40` : "none",
          }}
          onMouseEnter={(e) => {
            if (accountType !== "Corporate") {
              e.currentTarget.style.borderColor = tenant.theme.colorPrimary;
              e.currentTarget.style.transform = "translateY(-2px)";
            }
          }}
          onMouseLeave={(e) => {
            if (accountType !== "Corporate") {
              e.currentTarget.style.borderColor = isDark ? "#303030" : "#d9d9d9";
              e.currentTarget.style.transform = "translateY(0)";
            }
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "12px",
                backgroundColor: accountType === "Corporate" ? tenant.theme.colorPrimary : isDark ? "#303030" : "#e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease",
              }}
            >
              <BankOutlined style={{ fontSize: "28px", color: "#ffffff" }} />
            </div>
            <div style={{ flex: 1 }}>
              <Title
                level={5}
                style={{
                  margin: "0 0 6px 0",
                  color: isDark ? "#ffffff" : "#000000",
                  fontSize: "18px",
                }}
              >
                Corporate Account
              </Title>
              <Text style={{ 
                color: isDark ? "#a0a0a0" : "#666666",
                fontSize: "14px",
                display: "block",
                marginBottom: "8px",
              }}>
                Designed for businesses, SMEs, and organizations
              </Text>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Bulk payments
                </Text>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Team management
                </Text>
                <Text style={{ 
                  fontSize: "12px", 
                  color: tenant.theme.colorPrimary,
                  backgroundColor: isDark ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.1)",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}>
                  ✓ Advanced analytics
                </Text>
              </div>
            </div>
            <Radio checked={accountType === "Corporate"} />
          </div>
        </div>
      </Space>

      <Button
        type="primary"
        block
        size="large"
        onClick={handleNext}
        disabled={!accountType}
        style={{
          marginTop: "32px",
          backgroundColor: !accountType 
            ? (isDark ? "#2a2a2a" : "#f0f0f0")
            : tenant.theme.colorPrimary,
          borderColor: !accountType
            ? (isDark ? "#2a2a2a" : "#d9d9d9")
            : tenant.theme.colorPrimary,
          color: !accountType
            ? (isDark ? "#666666" : "#bfbfbf")
            : "#ffffff",
          height: "48px",
          fontSize: "16px",
          fontWeight: "500",
          opacity: !accountType ? 0.6 : 1,
          cursor: !accountType ? "not-allowed" : "pointer",
        }}
      >
        Continue
      </Button>
    </div>
  );

  const renderPersonalInformation = () => (
    <div style={{ marginTop: "32px" }}>
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              First Name
            </span>
          }
          name="firstName"
          rules={[{ required: true, message: "Please enter your first name" }]}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Enter your first name"
            size="large"
            value={formData.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Last Name
            </span>
          }
          name="lastName"
          rules={[{ required: true, message: "Please enter your last name" }]}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Enter your last name"
            size="large"
            value={formData.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Email Address
            </span>
          }
          name="email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            prefix={<MailOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Enter your email address"
            size="large"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Phone Number
            </span>
          }
          name="phoneNumber"
          rules={[
            { required: true, message: "Please enter your phone number" },
          ]}
        >
          <Input
            prefix={<PhoneOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Enter your phone number"
            size="large"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
          />
        </Form.Item>
      </Form>

      <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
        <Button
          size="large"
          onClick={handleBack}
          style={{
            flex: 1,
            height: "48px",
            fontSize: "16px",
          }}
        >
          Back
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleNext}
          style={{
            flex: 2,
            backgroundColor: tenant.theme.colorPrimary,
            borderColor: tenant.theme.colorPrimary,
            height: "48px",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          Continue
        </Button>
      </div>
    </div>
  );

  const renderAccountDetails = () => (
    <div style={{ marginTop: "32px" }}>
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Username
            </span>
          }
          name="username"
          rules={[
            { required: true, message: "Please enter a username" },
            {
              min: 4,
              message: "Username must be at least 4 characters long",
            },
          ]}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Choose a username"
            size="large"
            value={formData.username}
            onChange={(e) => handleInputChange("username", e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Password
            </span>
          }
          name="password"
          rules={[
            { required: true, message: "Please enter a password" },
            {
              min: 8,
              message: "Password must be at least 8 characters long",
            },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Create a password"
            size="large"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        <Form.Item
          label={
            <span style={{ color: isDark ? "#ffffff" : "#000000" }}>
              Confirm Password
            </span>
          }
          name="confirmPassword"
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match!"));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: "#8c8c8c" }} />}
            placeholder="Confirm your password"
            size="large"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            iconRender={(visible) =>
              visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>

        {/* Terms and Conditions */}
        <Form.Item>
          <Checkbox
            checked={formData.agreeToTerms}
            onChange={(e) =>
              handleInputChange("agreeToTerms", e.target.checked)
            }
            style={{ color: isDark ? "#ffffff" : "#000000" }}
          >
            I agree to the{" "}
            <Link
              style={{ color: tenant.theme.colorPrimary }}
              onClick={() => window.open("/terms", "_blank")}
            >
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link
              style={{ color: tenant.theme.colorPrimary }}
              onClick={() => window.open("/privacy", "_blank")}
            >
              Privacy Policy
            </Link>
          </Checkbox>
        </Form.Item>
      </Form>

      <div style={{ display: "flex", gap: "12px" }}>
        <Button
          size="large"
          onClick={handleBack}
          style={{
            flex: 1,
            height: "48px",
            fontSize: "16px",
          }}
        >
          Back
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!formData.agreeToTerms}
          style={{
            flex: 2,
            backgroundColor: !formData.agreeToTerms
              ? (isDark ? "#2a2a2a" : "#f0f0f0")
              : tenant.theme.colorPrimary,
            borderColor: !formData.agreeToTerms
              ? (isDark ? "#2a2a2a" : "#d9d9d9")
              : tenant.theme.colorPrimary,
            color: !formData.agreeToTerms
              ? (isDark ? "#666666" : "#bfbfbf")
              : "#ffffff",
            height: "48px",
            fontSize: "16px",
            fontWeight: "500",
            opacity: !formData.agreeToTerms ? 0.6 : 1,
            cursor: !formData.agreeToTerms ? "not-allowed" : "pointer",
          }}
        >
          Create Account
        </Button>
      </div>
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? `linear-gradient(135deg, #0f0c29 0%, ${tenant.theme.colorPrimary}15 50%, #24243e 100%)`
          : `linear-gradient(135deg, ${tenant.theme.colorPrimary}20 0%, ${tenant.theme.colorPrimary}40 50%, ${tenant.theme.colorPrimary}30 100%)`,
        padding: "20px",
      }}
    >
      {/* Professional Animated Background */}
      <style>
        {`
          @keyframes floatSignup {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -30px) rotate(120deg); }
            66% { transform: translate(-20px, 20px) rotate(240deg); }
          }
          
          @keyframes pulseSignup {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(1.15); }
          }
          
          @keyframes driftSignup {
            0% { transform: translate(0, 0); }
            25% { transform: translate(20px, -20px); }
            50% { transform: translate(40px, 10px); }
            75% { transform: translate(20px, 30px); }
            100% { transform: translate(0, 0); }
          }
          
          @keyframes gradientWaveSignup {
            0% { transform: translateX(-50%) translateY(-50%); }
            50% { transform: translateX(-40%) translateY(-60%); }
            100% { transform: translateX(-50%) translateY(-50%); }
          }
          
          @keyframes shimmerSignup {
            0% { background-position: -1000px 0; }
            100% { background-position: 1000px 0; }
          }

          .signup-floating-shape {
            position: absolute;
            border-radius: 50%;
            filter: blur(60px);
            opacity: 0.4;
          }
          
          .signup-shape-1 {
            width: 400px;
            height: 400px;
            background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : `${tenant.theme.colorPrimary}30`};
            top: -100px;
            right: -100px;
            animation: floatSignup 25s ease-in-out infinite;
          }
          
          .signup-shape-2 {
            width: 300px;
            height: 300px;
            background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : `${tenant.theme.colorPrimary}25`};
            bottom: -80px;
            left: -80px;
            animation: floatSignup 20s ease-in-out infinite reverse;
          }
          
          .signup-shape-3 {
            width: 250px;
            height: 250px;
            background: ${isDark ? 'rgba(255, 255, 255, 0.06)' : `${tenant.theme.colorPrimary}20`};
            top: 50%;
            left: 20%;
            animation: pulseSignup 15s ease-in-out infinite;
          }
          
          .signup-particle {
            position: absolute;
            width: 3px;
            height: 3px;
            background: ${isDark ? 'rgba(255, 255, 255, 0.4)' : tenant.theme.colorPrimary};
            border-radius: 50%;
            box-shadow: 0 0 8px ${isDark ? 'rgba(255, 255, 255, 0.3)' : `${tenant.theme.colorPrimary}80`};
          }

          .signup-card-wrapper {
            position: relative;
            z-index: 1;
          }

          .signup-card-wrapper::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(
              45deg,
              ${tenant.theme.colorPrimary}40,
              transparent,
              ${tenant.theme.colorPrimary}40
            );
            border-radius: 22px;
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: -1;
            animation: shimmerSignup 3s infinite;
            background-size: 1000px 100%;
          }

          .signup-card-wrapper:hover::before {
            opacity: 0.5;
          }
        `}
      </style>

      {/* Animated Gradient Overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, ${tenant.theme.colorPrimary}15 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${tenant.theme.colorPrimary}10 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, ${tenant.theme.colorPrimary}12 0%, transparent 50%)
          `,
          animation: "gradientWaveSignup 20s ease infinite",
        }}
      />

      {/* Floating Animated Shapes */}
      <div className="signup-floating-shape signup-shape-1" />
      <div className="signup-floating-shape signup-shape-2" />
      <div className="signup-floating-shape signup-shape-3" />

      {/* Floating Particles */}
      {[...Array(15)].map((_, i) => (
        <div
          key={i}
          className="signup-particle"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `driftSignup ${12 + Math.random() * 8}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: 0.2 + Math.random() * 0.3,
          }}
        />
      ))}

      {/* Language Selector - Top Right */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          zIndex: 10,
          display: "flex",
          gap: "12px",
        }}
      >
        {/* Development Tenant Switcher - Only show on localhost */}
        {process.env.NODE_ENV === 'development' && 
         (window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1') && (
          <TenantSwitcher />
        )}
        <LanguageSelector />
      </div>

      {/* Back to Landing - Top Left */}
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/landing")}
        style={{
          position: "absolute",
          top: "20px",
          left: "20px",
          color: isDark ? "#ffffff" : "#666666",
          zIndex: 10,
          fontWeight: 500,
        }}
      >
        Back to Home
      </Button>

      {/* Signup Card */}
      <div className="signup-card-wrapper" style={{ width: "100%", maxWidth: "600px" }}>
        <div
          style={{
            backgroundColor: isDark ? "rgba(31, 31, 31, 0.95)" : "rgba(255, 255, 255, 0.98)",
            borderRadius: "20px",
            padding: "56px 48px",
            boxShadow: isDark
              ? "0 30px 80px rgba(0, 0, 0, 0.5)"
              : "0 30px 80px rgba(0, 0, 0, 0.2)",
            backdropFilter: "blur(10px)",
            position: "relative",
          }}
        >
        {/* Logo and Title */}
        <div style={{ textAlign: "center", marginBottom: "36px" }}>
          <img
            src={isDark ? tenant.darkLogo : tenant.logo}
            alt={tenant.name}
            style={{
              maxWidth: "100px",
              height: "auto",
              marginBottom: "20px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              filter: isDark ? "brightness(1.1)" : "none",
            }}
          />
          <Title
            level={2}
            style={{
              margin: "0 0 8px 0",
              color: isDark ? "#ffffff" : "#000000",
              fontSize: "28px",
              fontWeight: 600,
            }}
          >
            Create Your Account
          </Title>
          <Text
            style={{
              color: isDark ? "#a0a0a0" : "#666666",
              fontSize: "15px",
            }}
          >
            Join thousands of customers banking with {tenant.name}
          </Text>
        </div>

        {/* Progress Steps */}
        <Steps
          current={currentStep}
          size="small"
          style={{ marginBottom: "32px" }}
        >
          <Step title="Account Type" />
          <Step title="Personal Info" />
          <Step title="Account Details" />
        </Steps>

        {/* Step Content */}
        {currentStep === 0 && renderAccountTypeSelection()}
        {currentStep === 1 && renderPersonalInformation()}
        {currentStep === 2 && renderAccountDetails()}

          {/* Login Link */}
          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <Text style={{ color: isDark ? "#8c8c8c" : "#595959" }}>
              Already have an account?{" "}
              <Link
                onClick={() => navigate("/authentication/login")}
                style={{
                  color: tenant.theme.colorPrimary,
                  fontWeight: "500",
                }}
              >
                Sign in
              </Link>
            </Text>
          </div>

          {/* Powered by BankLingo */}
          <div
            style={{
              textAlign: "center",
              marginTop: "32px",
              paddingTop: "24px",
              borderTop: `1px solid ${isDark ? "#303030" : "#f0f0f0"}`,
            }}
          >
            <Text
              style={{
                color: isDark ? "#8c8c8c" : "#8c8c8c",
                fontSize: "12px",
              }}
            >
              {tenant.name} • Powered by{" "}
              <span style={{ fontWeight: "600" }}>BankLingo</span>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
